/*eslint max-lines-per-function: ["error", 250]*/
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { isEqual } from 'lodash';

import { ExaminedEditor } from '../Examined/ExaminedEditor';
import { CompanyEditor } from '../Company/CompanyEditor';
import ValidationAlert from '../../../app/components/ValidationAlert';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import {
  CreateUpdateSalesInvoiceDto,
  GetCompanyDto,
  GetExaminedDto,
  GetSalesInvoiceDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  newSalesInvoice,
  convertSalesInvoice,
} from '../../../common/spdCore/contexts/SalesInvoiceContext';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import { SalesInvoiceEditorTabs } from '../../containers/SalesInvoiceEditorView/SalesInvoiceEditorView';
import { examinedData } from '../../../common/spdCore/contexts/ExaminedContext';
import { companyData } from '../../../common/spdCore/contexts/CompanyContext';

import { SalesInvoiceBasicDataEditor } from './SalesInvoiceBasicDataEditor';
import { SalesInvoiceBasicDataControls } from './SalesInvoiceBasicDataControls';

interface SalesInvoiceBasicDataProps {
  salesInvoice?: GetSalesInvoiceDto;
  salesInvoiceId?: string;
  setSalesInvoiceId: Dispatch<SetStateAction<string | undefined>>;
  setNotSavedTab: (tab: SalesInvoiceEditorTabs | undefined) => void;
  setActiveTab: Dispatch<SetStateAction<string>>;
  refreshSalesInvoice: () => Promise<void>;
  readOnly?: boolean;
}

export const SalesInvoiceBasicData: React.FC<
  SalesInvoiceBasicDataProps
> = props => {
  const [showExaminedEditor, setShowExaminedEditor] = useState(false);
  const [salesInvoice, setSalesInvoice] =
    useState<CreateUpdateSalesInvoiceDto>(newSalesInvoice());
  const [originalSalesInvoice, setOriginalSalesInvoice] =
    useState<CreateUpdateSalesInvoiceDto>(salesInvoice);
  const [currentExamined, setCurrentExamined] = useState<
    GetExaminedDto | undefined
  >(undefined);
  const [examinedRefreshKey, setExaminedRefreshKey] = useState(0);
  const [showCompanyEditor, setShowCompanyEditor] = useState(false);
  const [currentCompany, setCurrentCompany] = useState<
    GetCompanyDto | undefined
  >(undefined);
  const [companyRefreshKey, setCompanyRefreshKey] = useState(0);
  const [currentRecipient, setCurrentRecipient] = useState<
    GetCompanyDto | undefined
  >(undefined);
  const [recipientRefreshKey, setRecipientRefreshKey] = useState(0);
  const [showRecipientEditor, setShowRecipientEditor] = useState(false);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const spd = useSpdCore();
  const auth = useAuth();
  // Finanse podstawowe wystawiają faktury tylko z badania — wystawca domyślny
  const canChangeIssuer = auth.check([Role.Admin, Role.Finance]);

  useEffect(() => {
    if (props.salesInvoice) {
      const converted = convertSalesInvoice(props.salesInvoice);
      setSalesInvoice(converted);
      setOriginalSalesInvoice(converted);
      setCurrentExamined(props.salesInvoice.examined);
      setCurrentCompany(props.salesInvoice.company);
      setCurrentRecipient(
        props.salesInvoice.recipient
          ? (props.salesInvoice.recipient as unknown as GetCompanyDto)
          : undefined,
      );
    }
  }, [props.salesInvoice]);

  useEffect(() => {
    if (!salesInvoice.examinedId) {
      setCurrentExamined(undefined);
      return;
    }
    if (salesInvoice.examinedId === props.salesInvoice?.examined?.id) {
      setCurrentExamined(props.salesInvoice?.examined);
      return;
    }
    spd.examineds
      .getExamined(salesInvoice.examinedId)
      .then(ex => setCurrentExamined(ex))
      .catch(() => setCurrentExamined(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesInvoice.examinedId]);

  useEffect(() => {
    setSalesInvoice(prev => ({
      ...prev,
      examinedNip: currentExamined?.nip ?? null,
      examinedAddress:
        (currentExamined?.invoiceAddress || currentExamined?.city) ?? null,
    }));
  }, [currentExamined]);

  useEffect(() => {
    if (!salesInvoice.companyId) {
      setCurrentCompany(undefined);
      return;
    }
    if (salesInvoice.companyId === props.salesInvoice?.company?.id) {
      setCurrentCompany(props.salesInvoice?.company);
      return;
    }
    spd.companies
      .get(salesInvoice.companyId)
      .then(co => setCurrentCompany(co))
      .catch(() => setCurrentCompany(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesInvoice.companyId]);

  useEffect(() => {
    setSalesInvoice(prev => ({
      ...prev,
      companyNip: currentCompany?.nip ?? null,
      companyTaxIdentifier: currentCompany?.taxIdentifier ?? null,
      companyAddress: currentCompany?.address ?? null,
    }));
  }, [currentCompany]);

  useEffect(() => {
    if (!salesInvoice.recipientId) {
      setCurrentRecipient(undefined);
      return;
    }
    if (salesInvoice.recipientId === props.salesInvoice?.recipientId) {
      setCurrentRecipient(
        props.salesInvoice?.recipient
          ? (props.salesInvoice.recipient as unknown as GetCompanyDto)
          : undefined,
      );
      return;
    }
    spd.companies
      .get(salesInvoice.recipientId)
      .then(co => setCurrentRecipient(co))
      .catch(() => setCurrentRecipient(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesInvoice.recipientId]);

  const propertyChange = (obj: Partial<CreateUpdateSalesInvoiceDto>) => {
    const updatedSalesInvoice = {
      ...salesInvoice,
      ...obj,
    };

    const dataChanged = !isEqual(updatedSalesInvoice, originalSalesInvoice);
    props.setNotSavedTab(
      dataChanged ? SalesInvoiceEditorTabs.BASIC_DATA : undefined,
    );

    setSalesInvoice(updatedSalesInvoice);
  };

  return (
    <>
      <Form className="d-grid gap-4 mb-3">
        <SalesInvoiceBasicDataEditor
          salesInvoice={salesInvoice}
          propertyChange={propertyChange}
          company={currentCompany}
          examined={currentExamined}
          readOnly={props.readOnly}
          issuerReadOnly={
            props.readOnly ||
            !canChangeIssuer ||
            !!salesInvoice.number ||
            !!salesInvoice.originalInvoiceId
          }
          onEditExamined={() => setShowExaminedEditor(true)}
          examinedRefreshKey={examinedRefreshKey}
          onEditCompany={() => setShowCompanyEditor(true)}
          companyRefreshKey={companyRefreshKey}
          recipient={currentRecipient}
          onEditRecipient={() => setShowRecipientEditor(true)}
          recipientRefreshKey={recipientRefreshKey}
        />
        {salesInvoice.companyId && currentCompany && (
          <CompanyEditor
            id={salesInvoice.companyId}
            company={companyData(currentCompany)}
            batteries={currentCompany?.companiesBatteries?.map(
              cb => cb.battery,
            )}
            show={showCompanyEditor}
            handleClose={() => {
              setShowCompanyEditor(false);
              setCompanyRefreshKey(k => k + 1);
              if (salesInvoice.companyId) {
                spd.companies
                  .get(salesInvoice.companyId)
                  .then(co => setCurrentCompany(co))
                  .catch(() => undefined);
              }
            }}
            readOnly={props.readOnly ?? false}
          />
        )}
        {salesInvoice.recipientId && currentRecipient && (
          <CompanyEditor
            id={salesInvoice.recipientId}
            company={companyData(currentRecipient)}
            batteries={currentRecipient?.companiesBatteries?.map(
              cb => cb.battery,
            )}
            show={showRecipientEditor}
            handleClose={() => {
              setShowRecipientEditor(false);
              setRecipientRefreshKey(k => k + 1);
              if (salesInvoice.recipientId) {
                spd.companies
                  .get(salesInvoice.recipientId)
                  .then(co => setCurrentRecipient(co))
                  .catch(() => undefined);
              }
            }}
            readOnly={props.readOnly ?? false}
          />
        )}
        {salesInvoice.examinedId && currentExamined && (
          <ExaminedEditor
            id={salesInvoice.examinedId}
            examined={examinedData(currentExamined)}
            show={showExaminedEditor}
            handleClose={() => {
              setShowExaminedEditor(false);
              setExaminedRefreshKey(k => k + 1);
              if (salesInvoice.examinedId) {
                spd.examineds
                  .getExamined(salesInvoice.examinedId)
                  .then(ex => setCurrentExamined(ex))
                  .catch(() => undefined);
              }
            }}
            readOnly={props.readOnly ?? false}
          />
        )}
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="m-3"
        />
        <div className="d-flex">
          {props.salesInvoiceId && (
            <LastEditorInfo
              id={props.salesInvoiceId}
              className="color-primary"
              method={spd.salesInvoices.getLastEditor}
              additionalText="Ostatnia modyfikacja"
            />
          )}
          <SalesInvoiceBasicDataControls
            salesInvoice={salesInvoice}
            originalSalesInvoice={originalSalesInvoice}
            salesInvoiceId={props.salesInvoiceId}
            salesInvoiceDto={props.salesInvoice}
            setSalesInvoiceId={props.setSalesInvoiceId}
            setNotSavedTab={props.setNotSavedTab}
            setSaveErrors={setSaveErrors}
            setShowSaveErrors={setShowSaveErrors}
            setOriginalSalesInvoice={setOriginalSalesInvoice}
            setActiveTab={props.setActiveTab}
            refreshSalesInvoice={props.refreshSalesInvoice}
            readOnly={props.readOnly}
          />
        </div>
      </Form>
    </>
  );
};
